import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Product } from '@/types'

export interface CartItem {
  product: Product
  quantity: number
  sim_format?: 'physical' | 'esim'
}

export interface DeliveryOption {
  id: string
  name: string
  price: number
  description: string
}

interface CartState {
  items: CartItem[]
  discountCode: string | null
  discountAmount: number
  discountPercentage: number
  deliveryOption: DeliveryOption | null
  shippingAddress: any | null
  referralCode: string | null

  // Actions
  addItem: (product: Product, quantity?: number) => void
  removeItem: (productId: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  setSimFormat: (productId: number, format: 'physical' | 'esim') => void
  replaceItems: (items: CartItem[]) => void
  clearCart: () => void
  applyDiscountCode: (code: string, amount: number, percentage: number) => void
  clearDiscount: () => void
  setDeliveryOption: (option: DeliveryOption) => void
  setShippingAddress: (address: any) => void
  setReferralCode: (code: string | null) => void
  clearReferralCode: () => void

  // Computed
  getSubtotal: () => number
  getDiscountAmount: () => number
  getDeliveryFee: () => number
  getTax: () => number
  getNetAmount: () => number
  getTotal: () => number
  getItemCount: () => number
  hasContractItems: () => boolean
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      discountCode: null,
      discountAmount: 0,
      discountPercentage: 0,
      deliveryOption: null,
      shippingAddress: null,
      referralCode: null,

      addItem: (product, quantity = 1) => {
        const items = get().items
        const existingItem = items.find((item) => item.product.id === product.id)

        if (existingItem) {
          set({
            items: items.map((item) =>
              item.product.id === product.id
                ? { ...item, quantity: item.quantity + quantity }
                : item
            ),
          })
        } else {
          set({ items: [...items, { product, quantity }] })
        }
      },

      removeItem: (productId) => {
        set({ items: get().items.filter((item) => item.product.id !== productId) })
      },

      setSimFormat: (productId, format) => {
        set({
          items: get().items.map((item) =>
            item.product.id === productId ? { ...item, sim_format: format } : item
          ),
        })
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }
        set({
          items: get().items.map((item) =>
            item.product.id === productId ? { ...item, quantity } : item
          ),
        })
      },

      // Replace the whole item list wholesale (used when loading/merging the
      // server-saved cart on login).
      replaceItems: (items) => set({ items }),

      clearCart: () => {
        set({
          items: [],
          discountCode: null,
          discountAmount: 0,
          discountPercentage: 0,
          deliveryOption: null,
          shippingAddress: null,
          referralCode: null,
        })
      },

      applyDiscountCode: (code, amount, percentage) => {
        set({
          discountCode: code,
          discountAmount: amount,
          discountPercentage: percentage,
        })
      },

      clearDiscount: () => {
        set({
          discountCode: null,
          discountAmount: 0,
          discountPercentage: 0,
        })
      },

      setDeliveryOption: (option) => set({ deliveryOption: option }),

      setShippingAddress: (address) => set({ shippingAddress: address }),

      setReferralCode: (code) => set({ referralCode: code }),
      clearReferralCode: () => set({ referralCode: null }),

      getSubtotal: () => {
        return get().items.reduce((total, item) => {
          const price = item.product.discounted_price || item.product.price
          return total + price * item.quantity
        }, 0)
      },

      getDiscountAmount: () => {
        const state = get()
        const subtotal = state.getSubtotal()

        if (state.discountPercentage > 0) {
          return (subtotal * state.discountPercentage) / 100
        }
        return state.discountAmount
      },

      getDeliveryFee: () => {
        return get().deliveryOption?.price || 0
      },

      getTax: () => {
        // UK VAT is 20% - prices are VAT INCLUSIVE
        // To extract VAT from an inclusive price: VAT = gross - (gross / 1.2)
        // This equals: gross * (1 - 1/1.2) = gross / 6
        const subtotal = get().getSubtotal()
        const discount = get().getDiscountAmount()
        const grossAmount = subtotal - discount
        // VAT = gross - net, where net = gross / 1.2
        return grossAmount - (grossAmount / 1.2)
      },

      getNetAmount: () => {
        // Get the net amount (excluding VAT)
        // Net = Gross / 1.2
        const subtotal = get().getSubtotal()
        const discount = get().getDiscountAmount()
        const grossAmount = subtotal - discount
        return grossAmount / 1.2
      },

      getTotal: () => {
        // Total = subtotal - discount + delivery
        // VAT is already INCLUDED in subtotal, so we don't add it
        const subtotal = get().getSubtotal()
        const discount = get().getDiscountAmount()
        const delivery = get().getDeliveryFee()
        return subtotal - discount + delivery
      },

      getItemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0)
      },

      hasContractItems: () => {
        return get().items.some((item) => item.product.is_contract)
      },
    }),
    {
      name: 'cart-storage',
    }
  )
)
